// /analytics — overview cards + breakdowns.

import { useEffect, useState } from 'react';
import { ApiError, analytics, type AnalyticsOverview, type DailyStat, type ProviderStat } from '../api.js';

function fmt(n: number): string {
  return n.toLocaleString();
}

function fmtSeconds(s: number): string {
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export function AnalyticsPage() {
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [daily, setDaily] = useState<DailyStat[]>([]);
  const [providers, setProviders] = useState<ProviderStat[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setOverview(null);
    Promise.all([analytics.overview(days), analytics.daily(days), analytics.providers(days)])
      .then(([o, d, p]) => {
        if (cancelled) return;
        setOverview(o);
        setDaily(d.daily);
        setProviders(p.providers);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load.');
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  const dailyMax = Math.max(1, ...daily.map((d) => d.renders + d.directs));
  const providerMax = Math.max(1, ...providers.map((p) => p.renders));

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl mb-1">Analytics</h1>
          <p className="text-muted text-sm">Usage across all your projects.</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value, 10))}
          className="px-3 py-2 bg-parchment/60 border border-soft rounded text-sm"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {error && (
        <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {!overview ? (
        <p className="text-muted text-sm">loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <Card label="Renders" value={fmt(overview.total_renders)} />
            <Card label="Directs" value={fmt(overview.total_directs)} />
            <Card label="Audio generated" value={fmtSeconds(overview.total_seconds_generated)} />
            <Card label="Unique voices" value={fmt(overview.unique_voices_used)} />
          </div>

          <section className="mb-8">
            <h2 className="font-serif text-xl mb-3">Daily activity</h2>
            <div className="border border-soft rounded-lg bg-white px-5 py-4">
              {daily.length === 0 ? (
                <p className="text-xs text-muted">No activity in this window.</p>
              ) : (
                <div className="space-y-1">
                  {daily.map((d) => {
                    const total = d.renders + d.directs;
                    const pct = (total / dailyMax) * 100;
                    return (
                      <div key={d.date} className="flex items-center gap-3 text-xs">
                        <span className="text-muted font-mono w-20">{d.date}</span>
                        <div className="flex-1 bg-soft/40 rounded h-4 overflow-hidden relative">
                          <div
                            className="bg-accent/70 h-full"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-muted w-32 text-right">
                          {d.directs} direct · {d.renders} render
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          <section className="mb-8">
            <h2 className="font-serif text-xl mb-3">Providers</h2>
            <div className="border border-soft rounded-lg bg-white px-5 py-4">
              {providers.length === 0 ? (
                <p className="text-xs text-muted">No render activity in this window.</p>
              ) : (
                <div className="space-y-2">
                  {providers.map((p) => {
                    const pct = (p.renders / providerMax) * 100;
                    return (
                      <div key={p.provider} className="flex items-center gap-3 text-xs">
                        <span className="font-mono w-24">{p.provider}</span>
                        <div className="flex-1 bg-soft/40 rounded h-5 overflow-hidden">
                          <div className="bg-accent/70 h-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-muted w-40 text-right">
                          {p.renders} renders · {fmtSeconds(p.total_seconds)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          <section>
            <h2 className="font-serif text-xl mb-3">Providers used</h2>
            <div className="flex flex-wrap gap-2">
              {overview.providers_used.length === 0 ? (
                <p className="text-xs text-muted">No providers used yet.</p>
              ) : (
                overview.providers_used.map((p) => (
                  <span key={p} className="text-xs bg-soft/60 px-2 py-1 rounded font-mono">
                    {p}
                  </span>
                ))
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-soft rounded-lg bg-white px-5 py-4">
      <div className="text-xs text-muted uppercase tracking-wider">{label}</div>
      <div className="font-serif text-2xl mt-1">{value}</div>
    </div>
  );
}
